/**
 * Spec 219 — phone input wrapper for the web client.
 *
 * Wraps `react-international-phone`'s `PhoneInput` so the rest of the
 * app deals with a single component contract: `value` is always
 * E.164 (or empty) on the way in, the `onChange` callback emits
 * E.164, and the dial-code/flag picker is opinionated by default
 * to ES.
 *
 * Tokens come from `web/src/theme/tokens.css` — the underlying lib
 * ships its own SCSS, which we override with className props that
 * point at local CSS rules. The result blends with InlineEditField,
 * NewWork inputs, etc. without leaking the lib's defaults.
 *
 * Lazy hint: importing this file pulls a `~12 KB gzip` chunk; the
 * heavy `libphonenumber-js/min` (~75 KB metadata) is loaded on
 * demand by `web/src/utils/phone.ts` only when callers reach for
 * `normalizeE164`/`formatPhoneForDisplay`. The component itself
 * runs without the metadata (the picker uses its own bundled
 * country list).
 */
import { useEffect, useState } from 'react';
import { PhoneInput as RIPhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

import {
  getDefaultCountry,
  parseCountryFromE164,
  setDefaultCountry,
  type CountryIso2,
} from '../utils/phone';

import './PhoneInput.css';

interface PhoneInputProps {
  /** Current value in E.164 (`+34612345678`) or empty string. */
  value: string;
  /** Fires with the updated E.164 string (or '' when blank). */
  onChange: (next: string) => void;
  /** Optional input id for label `htmlFor`. */
  id?: string;
  /** Disabled state — locks both picker + input. */
  disabled?: boolean;
  /** ARIA label propagated to the underlying `<input>`. */
  ariaLabel?: string;
  /** Stable test id; the wrapper gets `${testId}` and the input
   *  gets `${testId}-field`. */
  testId?: string;
  /** Surface validation state — paints a red border when 'error'. */
  status?: 'idle' | 'error';
  /** Placeholder shown inside the input. */
  placeholder?: string;
  /** Fired on blur — useful so callers can run their own
   *  normalize-on-blur logic without subscribing to keystrokes. */
  onBlur?: () => void;
  /** ARIA describes the inline error element by id, when present. */
  ariaDescribedBy?: string;
  /** ARIA invalid flag mirrored to the underlying input. */
  ariaInvalid?: boolean;
  /** Optional ref-style id on the underlying <input> for tests. */
  autoComplete?: string;
}

export function PhoneInput({
  value,
  onChange,
  id,
  disabled,
  ariaLabel,
  testId,
  status = 'idle',
  placeholder = '612 345 678',
  onBlur,
  ariaDescribedBy,
  ariaInvalid,
  autoComplete = 'tel',
}: PhoneInputProps) {
  // Resolve default country once per mount: prefer the country
  // implied by the saved E.164 value, fall back to the per-session
  // preference. We can't await here, so the initial render uses the
  // sync default; the effect below upgrades to the parsed country
  // once `libphonenumber-js/min` lands.
  const [defaultCountry, setDefaultCountryState] = useState<CountryIso2>(() => getDefaultCountry());
  useEffect(() => {
    let cancelled = false;
    if (!value) return undefined;
    parseCountryFromE164(value).then((iso) => {
      if (cancelled || !iso) return;
      setDefaultCountryState(iso);
    });
    return () => {
      cancelled = true;
    };
  }, [value]);

  return (
    <div
      className={`pi-root pi-root--${status}`}
      data-testid={testId}
    >
      <RIPhoneInput
        defaultCountry={defaultCountry.toLowerCase()}
        value={value}
        onChange={(phone, meta) => {
          // Persist the user's chosen default country so a return
          // visit reflects their preference. Ignore the change if
          // they're still editing under the same country.
          const iso = meta.country.iso2.toUpperCase();
          if (iso && iso !== defaultCountry) {
            setDefaultCountry(iso);
            setDefaultCountryState(iso);
          }
          // Empty input → emit '' so the upstream form treats it as
          // unset. The lib emits the bare dial code (e.g. '+34')
          // when only the prefix is selected; we collapse that to ''
          // so saving a half-typed prefix doesn't pollute storage.
          // Two flavours of "no number entered yet": empty trimmed
          // input (just whitespace) AND a value equal to the bare
          // dial code (lib's settled-empty state). Both map to ''.
          const trimmed = meta.inputValue.trim();
          const dialCodePrefix = `+${meta.country.dialCode}`;
          const phoneStripped = phone.replace(/\s+/g, '');
          if (!trimmed || phoneStripped === dialCodePrefix) {
            onChange('');
            return;
          }
          onChange(phone);
        }}
        inputProps={
          {
            id,
            disabled,
            'aria-label': ariaLabel,
            'aria-describedby': ariaDescribedBy,
            'aria-invalid': ariaInvalid,
            // `data-testid` isn't part of the React HTML attr type but
            // every browser passes it through unchanged. Cast through
            // `Record<string, unknown>` to silence TS without losing
            // the inputProps type at the call site.
            'data-testid': testId ? `${testId}-field` : undefined,
            autoComplete,
            onBlur,
            placeholder,
          } as unknown as React.InputHTMLAttributes<HTMLInputElement>
        }
        className="pi-wrap"
        inputClassName="pi-input"
        countrySelectorStyleProps={{
          buttonClassName: 'pi-country-button',
          dropdownStyleProps: {
            className: 'pi-country-dropdown',
            listItemClassName: 'pi-country-item',
          },
        }}
      />
    </div>
  );
}
